import React from 'react';

/**
 * The summary and description pair, shared by the region and appellation
 * editors so the two cannot drift.
 *
 * Two fields because they do different jobs. `summary` is one sentence for a
 * search result, a card and an og:description, where length is capped by what
 * Google will render. `description` is body copy for the page. Deriving the
 * first by truncating the second is how a meta tag ends up stopping mid-clause,
 * which is the whole reason they are stored separately.
 *
 * The counter warns at 150 rather than blocking. 150 is the length a search
 * result reliably renders, not a rule the data has to obey: a 154 character
 * summary is worth flagging and not worth refusing. The model refuses past 200,
 * which is where a summary has stopped being one.
 */
const SUMMARY_TARGET = 150;

export default function CopyFields({
  idPrefix,
  summary = '',
  description = '',
  onChange,
  descriptionRows = 6,
  summaryHint = null,
}) {
  const used = (summary || '').length;
  const over = used > SUMMARY_TARGET;

  return (
    <>
      <div>
        <div className="flex items-baseline justify-between">
          <label htmlFor={`${idPrefix}-summary`} className="block text-sm font-medium text-gray-700">
            Summary
          </label>
          <span
            className={`text-xs tabular-nums ${over ? 'text-red-700 font-semibold' : 'text-gray-500'}`}
          >
            {used}/{SUMMARY_TARGET}
          </span>
        </div>
        <textarea
          id={`${idPrefix}-summary`}
          rows={2}
          value={summary || ''}
          onChange={(e) => onChange('summary', e.target.value)}
          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-red-800 focus:border-red-800 sm:text-sm"
        />
        <p className={`mt-1 text-xs ${over ? 'text-red-700' : 'text-gray-500'}`}>
          {over
            ? `${used - SUMMARY_TARGET} over. Search results cut off around ${SUMMARY_TARGET} characters.`
            : summaryHint
              || 'One sentence. Used for search results and link previews, not shown on the page.'}
        </p>
      </div>

      <div>
        <label htmlFor={`${idPrefix}-description`} className="block text-sm font-medium text-gray-700">
          Description
        </label>
        <textarea
          id={`${idPrefix}-description`}
          rows={descriptionRows}
          value={description || ''}
          onChange={(e) => onChange('description', e.target.value)}
          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-red-800 focus:border-red-800 sm:text-sm"
        />
        <p className="mt-1 text-xs text-gray-500">
          Body copy shown on the public page. A blank line separates paragraphs.
        </p>
      </div>
    </>
  );
}
